import React, { useState, useEffect } from "react";
import api from "../../globals/axios";
import {
  Container,
  Typography,
  Grid,
  Box,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { CustomTitle } from "../../components/Custom/CustomTypography";
import CustomAppBar from "../../components/AppBar";
import LoaderContainer from "../../components/LoaderContainer";
import GenericButton from "../../components/Custom/GenericButton";

const BuyingOffers = () => {
  const { t } = useTranslation();
  const [buyingInterests, setBuyingInterests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedInterest, setSelectedInterest] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");

  const fetchBuyingInterests = async () => {
    try {
      const response = await api.get("/fpo/buying-interests/");
      setBuyingInterests(response.data);
    } catch (error) {
      console.error("Error fetching buying interests:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBuyingInterests();
  }, []);

  const handleAccept = async (id) => {
    try {
      await api.patch(`/fpo/buying-interests/${id}/update-status/`, {
        status: "Accepted",
      });
      fetchBuyingInterests();
    } catch (error) {
      console.error("Error accepting buying interest:", error);
    }
  };

  const handleReject = (interest) => {
    setSelectedInterest(interest);
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setSelectedInterest(null);
    setRejectionReason("");
  };

  const handleSubmitRejection = async () => {
    if (selectedInterest) {
      try {
        await api.patch(
          `/fpo/buying-interests/${selectedInterest.id}/update-status/`,
          {
            status: "Rejected",
            rejection_reason: rejectionReason,
          }
        );
        handleDialogClose();
        fetchBuyingInterests();
      } catch (error) {
        console.error("Error rejecting buying interest:", error);
      }
    }
  };

  return (
    <>
      <CustomAppBar />
      <LoaderContainer loading={loading}>
        <Container maxWidth="xs">
          <CustomTitle>{t("Buying Offers")}</CustomTitle>
          <Grid container spacing={2} sx={{ marginTop: "1px" }}>
            {buyingInterests.length === 0 ? (
              <Box
                sx={{
                  height: "80vh",
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ mb: 1, textAlign: "center" }}
                >
                  {t("No Offers found")}
                </Typography>
              </Box>
            ) : (
              buyingInterests.map((interest) => {
                const detailData = [
                  { label: t("Commodity :"), value: interest.commodity.name },
                  { label: t("Quantity :"), value: `${interest.quantity} MT` },
                  { label: t("District :"), value: interest.district.name },
                  { label: t("Date :"), value: interest.date },
                  { label: t("Pin Code :"), value: interest.pin_code },
                  {
                    label: t("Name :"),
                    value: interest.buyer_details.contact_person_name,
                  },
                  {
                    label: t("Contact :"),
                    value: interest.buyer_details.contact_person_phone_number,
                  },
                ];

                return (
                  <Grid item xs={12} key={interest.id}>
                    <Card
                      sx={{ boxShadow: "none", border: "1px solid #A5A5A5" }}
                    >
                      <CardContent>
                        <Grid container spacing={1}>
                          {detailData.map((item, index) => (
                            <React.Fragment key={index}>
                              <Grid item xs={6}>
                                <Typography variant="body1">
                                  {item.label}
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="body1">
                                  {item.value}
                                </Typography>
                              </Grid>
                            </React.Fragment>
                          ))}
                        </Grid>

                        {/* Action Buttons */}
                        <Grid container spacing={1} sx={{ marginTop: 1 }}>
                          <Grid item xs={6}>
                            <GenericButton
                              onClick={() => handleAccept(interest.id)}
                              backgroundColor="#4CAF50"
                              fontColor="white"
                              padding="8px 0"
                              fontSize="14px"
                              disabled={interest.status === "Accepted"}
                              hideIcon
                            >
                              {t("Accept")}
                            </GenericButton>
                          </Grid>
                          <Grid item xs={6}>
                            <GenericButton
                              onClick={() => handleReject(interest)}
                              backgroundColor="#CF5C5C"
                              fontColor="white"
                              padding="8px 0"
                              fontSize="14px"
                              disabled={interest.status === "Rejected"}
                              hideIcon
                            >
                              {t("Reject")}
                            </GenericButton>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })
            )}
          </Grid>
        </Container>

        {/* Dialog for rejection reason */}
        <Dialog open={openDialog} onClose={handleDialogClose}>
          <DialogTitle>{t("Rejection Reason")}</DialogTitle>
          <DialogContent sx={{ paddingBottom: "10px" }}>
            <DialogContentText>
              {t("Please provide a reason for rejecting this offer.")}
            </DialogContentText>
            <TextField
              autoFocus
              label={t("Rejection Reason")}
              type="text"
              fullWidth
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              multiline
              rows={4}
              sx={{ marginTop: "15px" }}
            />
          </DialogContent>
          <DialogActions sx={{ padding: "0 24px" }}>
            <GenericButton
              onClick={handleDialogClose}
              backgroundColor="#4CAF50"
              fontColor="white"
              padding="8px 0"
              fontSize="14px"
              hideIcon
            >
              {t("Cancel")}
            </GenericButton>

            <GenericButton
              onClick={handleSubmitRejection}
              backgroundColor="#CF5C5C"
              fontColor="white"
              padding="8px 0"
              fontSize="14px"
              hideIcon
            >
              {t("Submit")}
            </GenericButton>
          </DialogActions>
        </Dialog>
      </LoaderContainer>
    </>
  );
};

export default BuyingOffers;
